/**
 * MentionAutocomplete — Dropdown for @mentions in chat input
 *
 * Shows when user types @ in message input.
 * Filters participants by name, shows @channel and @here for group/channel convos.
 */

import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationParticipant, Mention, ConversationType } from '../types';
import { ChatAvatar } from './ChatAvatar';

interface MentionAutocompleteProps {
  query: string;
  participants: ConversationParticipant[];
  conversationType: ConversationType;
  currentUserId: string;
  onSelect: (mention: Mention, displayName: string) => void;
  onClose: () => void;
}

export const MentionAutocomplete = memo(function MentionAutocomplete({
  query,
  participants,
  conversationType,
  currentUserId,
  onSelect,
  onClose,
}: MentionAutocompleteProps) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const isGroupOrChannel = conversationType === 'group' || conversationType === 'channel';
  const lowerQuery = query.toLowerCase();

  // Build options: broadcast mentions + filtered participants
  const options: Array<{ mention: Mention; displayName: string; avatar?: string | null }> = [];

  if (isGroupOrChannel) {
    if ('channel'.includes(lowerQuery) || !lowerQuery) {
      options.push({
        mention: { type: 'channel' },
        displayName: '@channel',
        avatar: null,
      });
    }
    if ('here'.includes(lowerQuery) || !lowerQuery) {
      options.push({
        mention: { type: 'here' },
        displayName: '@here',
        avatar: null,
      });
    }
  }

  for (const p of participants) {
    if (p.userId === currentUserId) continue;
    const name = p.user.name.toLowerCase();
    if (lowerQuery && !name.includes(lowerQuery)) continue;
    options.push({
      mention: { type: 'user', userId: p.userId },
      displayName: p.user.name,
      avatar: p.user.avatar,
    });
  }

  // Limit to 6
  const visibleOptions = options.slice(0, 6);

  // Reset selection when options change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, visibleOptions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (visibleOptions[selectedIndex]) {
          const opt = visibleOptions[selectedIndex];
          onSelect(opt.mention, opt.displayName);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIndex, visibleOptions, onSelect, onClose]);

  if (visibleOptions.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute bottom-full start-0 end-0 z-50 mx-3 mb-1 max-h-60 overflow-y-auto rounded-xl border glass-surface shadow-lg"
      style={{ borderColor: 'var(--user-page-card-border)' }}
    >
      {visibleOptions.map((opt, i) => (
        <button
          key={opt.mention.type === 'user' ? opt.mention.userId : opt.mention.type}
          type="button"
          className="flex w-full items-center gap-2.5 px-3 py-2 text-start text-sm transition-colors"
          style={i === selectedIndex ? { backgroundColor: 'var(--user-page-card-bg)' } : undefined}
          onMouseEnter={() => setSelectedIndex(i)}
          onClick={() => onSelect(opt.mention, opt.displayName)}
        >
          {opt.mention.type === 'user' ? (
            <ChatAvatar src={opt.avatar} name={opt.displayName} size="h-7 w-7" />
          ) : (
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-xs font-bold">
              @
            </div>
          )}
          <span className="font-medium" style={{ color: 'var(--user-page-heading)' }}>{opt.displayName}</span>
          {opt.mention.type === 'channel' && (
            <span className="text-xs" style={{ color: 'var(--user-page-text)' }}>{t('messaging.mentionChannel')}</span>
          )}
          {opt.mention.type === 'here' && (
            <span className="text-xs" style={{ color: 'var(--user-page-text)' }}>{t('messaging.mentionHere')}</span>
          )}
        </button>
      ))}
    </div>
  );
});
